import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const LAST_UPDATED = "June 2, 2026";

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy — LumberLoop USA";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20" data-testid="privacy-page">
      <div className="label-tag">Legal</div>
      <h1 className="font-heading text-5xl md:text-6xl font-black uppercase mt-2 leading-none text-[#1A365D]">
        Privacy Policy
      </h1>
      <p className="mt-3 font-mono-ll text-xs tracking-widest text-slate-500 uppercase">Last updated: {LAST_UPDATED}</p>

      <div className="brutal-card p-8 md:p-10 mt-10 space-y-8 text-slate-800 leading-relaxed">

        <Section title="1. Overview">
          <p>
            LumberLoop USA ("we," "us," "our") respects your privacy. This Privacy Policy explains what information we collect, how we use it, and the choices you have. By using the Service you agree to the practices described here.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p className="font-bold mt-2">From contractors submitting lead requests:</p>
          <ul className="list-disc pl-6 mt-1 space-y-1">
            <li>Name, company name (optional), email address, phone number</li>
            <li>Job-site ZIP code</li>
            <li>Product type, quantity, specifications, timeline, budget (optional), and notes</li>
            <li>City and state derived from your ZIP for routing purposes</li>
          </ul>
          <p className="font-bold mt-4">From manufacturers and dealers using the dashboard:</p>
          <ul className="list-disc pl-6 mt-1 space-y-1">
            <li>Account email, password (stored as a one-way bcrypt hash — we never see your plaintext password)</li>
            <li>Company name and contact name</li>
            <li>Product listings and dealer-network records you upload</li>
          </ul>
          <p className="font-bold mt-4">Automatic technical data:</p>
          <ul className="list-disc pl-6 mt-1 space-y-1">
            <li>Authentication cookies (httpOnly, secure, used solely to keep you signed in)</li>
            <li>Server logs (IP address, request timestamps, error traces)</li>
          </ul>
          <p className="mt-3">We do not knowingly collect information from anyone under 18.</p>
        </Section>

        <Section title="3. How We Use Information">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Lead routing:</strong> we match contractor leads to dealers in matching markets and email the relevant manufacturers.</li>
            <li><strong>Account access:</strong> to authenticate users and provide manufacturer dashboards.</li>
            <li><strong>Notifications:</strong> to send transactional emails (lead alerts, password resets) from our verified domain.</li>
            <li><strong>Service operation and security:</strong> to detect abuse, fix bugs, and improve the platform.</li>
            <li><strong>Public dealer locator:</strong> dealer name, location, phone, email, and parent manufacturer are publicly displayed on the Service's dealer locator page — this is intentional and is the purpose of submitting dealer records.</li>
          </ul>
        </Section>

        <Section title="4. How We Share Information">
          <p>We share your information only in these limited cases:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong>With matched manufacturers:</strong> when a contractor's lead matches a manufacturer's dealer network by ZIP or state, that contractor's submitted contact info and request details are emailed to the matched manufacturer for follow-up.</li>
            <li><strong>With service providers:</strong> we use Resend (transactional email), MongoDB (database hosting), and Emergent (application hosting). These providers process data on our behalf under their own privacy and security commitments.</li>
            <li><strong>For legal reasons:</strong> if required by a valid legal process, or to protect the rights, property, or safety of LumberLoop USA, our users, or others.</li>
            <li><strong>Business transfers:</strong> if LumberLoop USA is involved in a merger, acquisition, or asset sale, information may be transferred as part of that transaction with appropriate notice to affected users.</li>
          </ul>
          <p className="mt-3 font-bold">
            We do not sell your personal information. We do not share data with advertising networks. We do not run third-party tracking or analytics.
          </p>
        </Section>

        <Section title="5. Data Retention">
          <p>
            We retain lead and account data for as long as your account is active or as needed to provide the Service. You may request deletion of your account and associated data at any time by emailing <a href="mailto:info@lumberloopusa.com" className="text-[#C62828] font-bold hover:underline">info@lumberloopusa.com</a>. Some records (e.g., transaction logs needed for legal or accounting purposes) may be retained longer where required by law.
          </p>
        </Section>

        <Section title="6. Security">
          <p>We use industry-standard safeguards to protect your information, including:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>HTTPS encryption for all data in transit</li>
            <li>Bcrypt-hashed passwords (we never store or see your plaintext password)</li>
            <li>JWT-based authentication with short-lived tokens stored in secure httpOnly cookies</li>
            <li>Role-based access control — manufacturers can only see their own data; admin access is restricted</li>
            <li>Single-use, time-limited password-reset tokens</li>
          </ul>
          <p className="mt-3">No system is 100% secure, however. If we ever experience a data breach affecting your information, we will notify affected users in accordance with applicable law.</p>
        </Section>

        <Section title="7. Your Rights">
          <p>Depending on where you live, you may have the right to:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Access the information we hold about you</li>
            <li>Correct inaccurate or incomplete information</li>
            <li>Delete your account and associated data</li>
            <li>Opt out of non-essential email communications</li>
            <li>Object to or restrict certain processing of your information</li>
          </ul>
          <p className="mt-3">
            To exercise any of these rights, email us at <a href="mailto:info@lumberloopusa.com" className="text-[#C62828] font-bold hover:underline">info@lumberloopusa.com</a>. We'll respond within a reasonable timeframe (typically within 30 days).
          </p>
        </Section>

        <Section title="8. Cookies">
          <p>
            We use only essential authentication cookies. These cookies keep you signed in to your account and cannot be disabled while you use logged-in features of the Service. We do not use cookies for advertising, tracking across sites, or analytics.
          </p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>
            The Service is not intended for, or directed at, anyone under 18 years of age. We do not knowingly collect personal information from children under 18. If you believe a minor has provided information to us, please contact us and we will delete the information promptly.
          </p>
        </Section>

        <Section title="10. International Users">
          <p>
            The Service is operated from the United States. If you access the Service from outside the U.S., your information will be transferred to and processed in the U.S. By using the Service you consent to such transfer.
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. Material changes will be announced at the top of this page with an updated "Last updated" date. Continued use of the Service after changes become effective constitutes acceptance.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            Questions or concerns about this Privacy Policy or how we handle your information? Email us at <a href="mailto:info@lumberloopusa.com" className="text-[#C62828] font-bold hover:underline">info@lumberloopusa.com</a>.
          </p>
        </Section>

      </div>

      <div className="mt-8 text-sm text-slate-500">
        See also: <Link to="/terms" className="text-[#C62828] font-bold hover:underline" data-testid="privacy-to-terms">Terms of Service</Link>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-xl font-bold uppercase text-[#1A365D] mb-3 border-l-4 border-[#C62828] pl-3">{title}</h2>
      <div className="text-base">{children}</div>
    </section>
  );
}
